import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../auth/Auth.css';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [form, setForm] = useState({ password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setTokenError('Invalid reset link. Please request a new one.'); setValidating(false); return; }
    axios.get('/api/auth/validate-reset-token', { params: { token } })
      .then(() => { setTokenValid(true); setValidating(false); })
      .catch(err => { setTokenError(err.response?.data?.error || 'Invalid or expired reset link.'); setValidating(false); });
  }, [token]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(form.password)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/[0-9]/.test(form.password)) { setError('Password must contain at least one number.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post('/api/auth/reset-password', { token, password: form.password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-branding">
        <div className="auth-branding-inner">
          <div className="auth-logo"><img src="/wf-logo.png" alt="Wow Factor Baseball" style={{width:'192px',height:'192px',objectFit:'contain'}} /></div>
          <h1 className="auth-brand-name">WOW FACTOR</h1>
          <p className="auth-brand-sub">Baseball Training & Development</p>
          <div className="auth-brand-divider" />
          <p className="auth-brand-quote">Built different.<br />Train with purpose.</p>
        </div>
      </div>
      <div className="auth-form-panel">
        <div className="auth-form-container">
          {validating ? (
            <div style={{ textAlign:'center', color:'var(--text-muted)' }}>
              <span className="auth-spinner" style={{ borderColor:'var(--border)', borderTopColor:'var(--text-primary)', width:'32px', height:'32px', borderWidth:'3px' }} />
              <p style={{ marginTop:'16px' }}>Validating reset link...</p>
            </div>
          ) : !tokenValid ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'3rem', marginBottom:'16px' }}>⚠</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', letterSpacing:'0.05em', marginBottom:'12px', color:'var(--error)' }}>LINK EXPIRED</h2>
              <p style={{ color:'var(--text-secondary)', lineHeight:'1.6', marginBottom:'24px' }}>{tokenError}</p>
              <Link to="/forgot-password" className="btn btn-primary" style={{ display:'inline-flex' }}>Request New Link</Link>
            </div>
          ) : success ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(74,222,128,0.15)', border:'2px solid #4ade80', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', margin:'0 auto 20px' }}>✓</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', letterSpacing:'0.05em', marginBottom:'12px' }}>PASSWORD RESET!</h2>
              <p style={{ color:'var(--text-secondary)', marginBottom:'8px' }}>Your password has been updated successfully.</p>
              <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginBottom:'24px' }}>Redirecting to login in a moment...</p>
              <Link to="/login" className="btn btn-primary" style={{ display:'inline-flex' }}>Go to Login</Link>
            </div>
          ) : (
            <>
              <div className="auth-form-header">
                <h2>New Password</h2>
                <p>Enter your new password below</p>
              </div>
              <form onSubmit={handleSubmit} className="auth-form">
                {error && <div className="auth-error-banner"><span>⚠</span> {error}</div>}
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-input" placeholder="Min 8 chars, 1 uppercase, 1 number" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} required autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input type="password" className="form-input" placeholder="Repeat your password" value={form.confirm_password} onChange={e => setForm(f=>({...f,confirm_password:e.target.value}))} required />
                </div>
                <button type="submit" className="btn btn-primary btn-lg auth-submit-btn" disabled={loading}>
                  {loading ? <span className="auth-spinner" /> : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
